import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { update as updateUser } from 'redux/modules/users';
import {
  getAllTopics,
  getTopicsState,
  // toggleUserTopic,
} from 'redux/modules/topics';
import { datetimeToText } from '../Forum';
import TopicHeader from '../TopicHeader/TopicHeader';
import styles from './AllTopics.module.scss';
import { card } from '../Forum.module.scss';

const TopicComponent = ({
  value,
  name,
  icon,
  active,
  subscribed,
  nQuestions,
  nFollowers,
  path,
  updateUser,
  isAuth,
  user,
}) => {
  const activeText = useMemo(
    () => (
      <div>
        last active <span>{datetimeToText(active)}</span> ago
      </div>
    ),
    [active]
  );
  const handleSubscribe = useCallback(
    () =>
      isAuth
        ? subscribed
          ? updateUser({
              _id: user._id,
              subscribedTopics: (user.subscribedTopics || []).filter(
                topic => topic !== value
              ),
            })
          : updateUser({
              _id: user._id,
              subscribedTopics: [
                ...new Set([...(user.subscribedTopics || []), value]),
              ],
            })
        : null,
    [updateUser, value, isAuth, user, subscribed]
  );
  return (
    <div className={cx(card, styles.topic)}>
      <div>
        <Link to={path} className={cx(subscribed && styles.highlight)}>
          <FontAwesomeIcon icon={icon} />
          <span>{name}</span>
        </Link>
        {active ? activeText : <div>Not active</div>}
      </div>
      <div>
        <div className={styles.stats}>
          <div>{nQuestions}</div>
          <div>questions</div>
        </div>
        <div className={styles.stats}>
          <div>{nFollowers}</div>
          <div>followers</div>
        </div>
        <FontAwesomeIcon
          icon="rss"
          onClick={handleSubscribe}
          className={cx(
            subscribed && styles.highlight,
            !isAuth && styles.disable
          )}
        />
      </div>
    </div>
  );
};

TopicComponent.propTypes = {
  value: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  active: PropTypes.instanceOf(Date),
  subscribed: PropTypes.bool.isRequired,
  nQuestions: PropTypes.number.isRequired,
  nFollowers: PropTypes.number.isRequired,
  isAuth: PropTypes.bool.isRequired,
  user: PropTypes.object,
};

TopicComponent.defaultProps = {
  active: null,
  subscribed: false,
  nQuestions: 0,
  nFollowers: 0,
};

const Topic = connect(
  null,
  { updateUser }
)(TopicComponent);

function compare(a, b) {
  if (!a.active && !b.active) return 0;
  if (a.active && b.active) {
    if (a.active < b.active) return -1;
    if (a.active > b.active) return 1;
  }
  if (a.active && !b.active) return -1;
  if (!a.active && b.active) return 1;
  return 0;
}

const AllTopics = ({ topics, getAllTopics, isAuth, user }) => {
  let selectData = useMemo(() => {
    const base = [{ name: 'Active', value: 'active' }];
    if (!isAuth) return base;
    else return [{ name: 'Subscription', value: 'sub' }].concat(base);
  }, [isAuth]);
  const [search, setSearch] = useState('');
  const [select, setSelect] = useState(selectData[0].value);
  const handleSearch = useCallback(search => setSearch(search), []);
  const handleSelect = useCallback(select => setSelect(select), []);
  let list = [...topics];
  if (search)
    list = list.filter(({ name }) => name.toLowerCase().includes(search));
  if (select === 'active') list = list.slice().sort((a, b) => compare(a, b));
  else if (select === 'sub')
    list = list.sort((a, b) => !!b.subscribed - !!a.subscribed);
  useEffect(() => {
    getAllTopics();
  }, [getAllTopics]);
  return (
    <div>
      <Helmet>
        <title>All Topics - Forums</title>
      </Helmet>
      <TopicHeader
        header="SmartGuppy Forum - All Topics"
        searchVal={search}
        selectVal={select}
        selectData={selectData}
        onSearch={handleSearch}
        onSelect={handleSelect}
      />
      {list.map(topic => (
        <Topic key={topic.value} {...topic} isAuth={isAuth} user={user} />
      ))}
    </div>
  );
};

AllTopics.propTypes = {
  topics: PropTypes.array.isRequired,
  isAuth: PropTypes.bool.isRequired,
  user: PropTypes.object,
};

export default connect(
  state => {
    const {
      entities: { users },
    } = state;
    return {
      topics: getTopicsState(state).sort((a, b) =>
        a.name < b.name ? -1 : a.name > b.name ? 1 : 0
      ),
      isAuth: !!state.auth.user,
      user: state.auth.user && users[state.auth.user],
    };
  },
  { getAllTopics }
)(AllTopics);
